import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Car, Wrench, Store, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { HERO_IMG, VEHICLES } from "@/data/products";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";

export default function Home() {
  const { t, lang } = useApp();
  const icons = [Car, Store, Wrench];
  const preview = [VEHICLES[0], VEHICLES[4], VEHICLES[9], VEHICLES[11]];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="CEA30" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
        </div>
        <div className="relative mx-auto flex min-h-[82vh] max-w-7xl flex-col justify-center px-6 py-24 sm:px-8">
          <motion.span
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm"
          >
            {t.home.badge}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl text-5xl font-extrabold leading-[1.05] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            {t.home.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-blue-50/90 sm:text-lg"
          >
            {t.home.heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Button asChild className="rounded-full bg-cea-accent px-7 py-6 text-base font-semibold text-white hover:bg-white hover:text-cea-navy">
              <Link to="/flotte" data-testid="hero-fleet-btn">{t.cta.ourFleet}<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/40 bg-white/5 px-7 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white hover:text-cea-navy">
              <Link to="/catalogue" data-testid="hero-catalogue-btn">{t.cta.viewCatalogue}</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cea-accent">{t.home.servicesTitle}</p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {t.home.services.map((s, i) => {
            const Icon = icons[i];
            const dest = ["/flotte", "/catalogue", "/catalogue"][i];
            return (
              <Link key={i} to={dest} className="group rounded-2xl border border-slate-200 bg-cea-surface p-8 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-900/5 focus:outline-none focus:ring-2 focus:ring-cea-accent" data-testid={`service-${i}`}>
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-cea-navy text-white transition-colors group-hover:bg-cea-accent">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-cea-ink">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-cea-accent">
                  {t.detail.learnMore}<ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Fleet teaser */}
      <section className="bg-cea-surface py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-cea-navy sm:text-4xl">{t.home.fleetTeaser}</h2>
              <p className="mt-2 max-w-lg text-slate-500">{t.home.fleetTeaserSub}</p>
            </div>
            <Link to="/flotte" data-testid="fleet-teaser-link" className="group inline-flex items-center gap-1 text-sm font-semibold text-cea-accent hover:text-cea-navy">
              {t.cta.ourFleet}<ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {preview.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-cea-navy px-8 py-14 sm:px-14">
          <div className="relative z-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{t.home.ctaBannerTitle}</h2>
              <p className="mt-2 text-blue-100/80">{t.home.ctaBannerSub}</p>
            </div>
            <Button asChild className="rounded-full bg-white px-7 py-6 text-base font-semibold text-cea-navy hover:bg-cea-accent hover:text-white">
              <Link to="/devis" data-testid="cta-devis-btn">{t.cta.getQuote}<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-cea-accent/20 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
